import {type ConnectionOperation, OPTIMISTIC_KEY} from '@github-ui/optimistic-connection'
import {
  connectionMutationKey,
  type ConnectionMutationVariables,
} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {createSubIssueFromChecklistItemMutation as createSubIssueFromChecklistItemQueryId} from '@github-ui/server-persisted-queries'
import type {CreateSubIssueFromChecklistItemMutationMutation} from '@github-ui/server-persisted-queries/types/createSubIssueFromChecklistItemMutation'
import {isSubIssuesOptimisticOverlayEnabled} from '@github-ui/sub-issues/optimistic-overlay'
import {
  buildSubIssueNodeFromServer,
  buildSubIssueOptimisticNode,
  createTempSubIssueId,
} from '@github-ui/sub-issues/sub-issue-optimistic-node'
import {useCallback} from 'react'
import type {Environment, RecordProxy, RecordSourceProxy, Variables} from 'relay-runtime'

import type {ChecklistItemConversionRoute} from './checklist-item-conversion-helpers'
import {
  installIssueBodyInRelay,
  postChecklistItemConversion,
  settleIssueFields,
  throwPayloadErrors,
} from './checklist-item-conversion-helpers'

export type CreateSubIssueFromChecklistItemVariables = ChecklistItemConversionRoute & {
  body: string
  position: readonly number[]
  title: string
}

type CreateSubIssueMutationVariables = CreateSubIssueFromChecklistItemVariables & ConnectionMutationVariables

type Issue = NonNullable<
  NonNullable<CreateSubIssueFromChecklistItemMutationMutation['convertChecklistItemToSubIssue']>['issue']
>
type ParentIssue = NonNullable<Issue['parent']>
type SubIssueNode = NonNullable<NonNullable<ParentIssue['subIssues']['nodes']>[number]>
type IssueRecordFields = Omit<SubIssueNode, 'closed'> & {closed?: boolean}
type SuccessfulIssue = Omit<Issue, 'parent'> & {parent: ParentIssue}
type ConversionResult = {
  data: CreateSubIssueFromChecklistItemMutationMutation
  issue: SuccessfulIssue
}

// Relay keys connection records by field arguments, so these must match the arguments the reading
// queries use or the writes land under storage keys nothing reads.
const ASSIGNEES_ARGS = {first: 10}
const CLOSED_BY_PULL_REQUESTS_ARGS = {first: 0, includeClosedPrs: true}
const SUB_ISSUES_ARGS = {first: 100}
const SUB_ISSUES_CONNECTION_PATH = ['subIssues']

function buildAddOperation(
  node: ReturnType<typeof buildSubIssueOptimisticNode> & {[OPTIMISTIC_KEY]?: string},
): ConnectionOperation {
  return {
    type: 'addToConnection',
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    node,
    anchor: {at: 'end'},
    counts: [
      {path: ['subIssuesSummary', 'total'], delta: 1},
      {path: ['subIssuesConnection', 'totalCount'], delta: 1},
      ...(node.closed ? [{path: ['subIssuesSummary', 'completed'], delta: 1}] : []),
    ],
  }
}

function buildOptimisticAddOperation(title: string): ConnectionOperation {
  const id = createTempSubIssueId()
  return buildAddOperation(buildSubIssueOptimisticNode({id, title}))
}

function storageKey(field: string, args?: Variables): string {
  if (!args) return field
  return `${field}(${Object.entries(args)
    .map(([name, value]) => `${name}:${String(value)}`)
    .join(',')})`
}

function linkedRecord(
  store: RecordSourceProxy,
  owner: RecordProxy,
  field: string,
  typeName: string,
  args?: Variables,
): RecordProxy {
  const id = `client:${owner.getDataID()}:${storageKey(field, args)}`
  const record = owner.getLinkedRecord(field, args) ?? store.get(id) ?? store.create(id, typeName)
  owner.setLinkedRecord(record, field, args)
  return record
}

function writeActor(store: RecordSourceProxy, actor: SubIssueNode['repository']['owner']): RecordProxy {
  const record = store.get(actor.id) ?? store.create(actor.id, actor.__typename)
  record.setValue(actor.id, 'id')
  record.setValue(actor.login, 'login')
  return record
}

function writeRepository(store: RecordSourceProxy, repository: SubIssueNode['repository']): RecordProxy {
  const record = store.get(repository.id) ?? store.create(repository.id, 'Repository')
  record.setValue(repository.id, 'id')
  record.setValue(repository.name, 'name')
  record.setLinkedRecord(writeActor(store, repository.owner), 'owner')
  return record
}

function writeAssignees(store: RecordSourceProxy, issue: RecordProxy, assignees: SubIssueNode['assignees']): void {
  const connection = linkedRecord(store, issue, 'assignees', 'UserConnection', ASSIGNEES_ARGS)
  connection.setValue(assignees.totalCount, 'totalCount')
  const edges = (assignees.edges ?? []).map((edge, index) => {
    const id = `client:${issue.getDataID()}:${storageKey('assignees', ASSIGNEES_ARGS)}:edges:${index}`
    const edgeRecord = store.get(id) ?? store.create(id, 'UserEdge')
    const node = edge?.node
    if (node) {
      const user = store.get(node.id) ?? store.create(node.id, 'User')
      user.setValue(node.id, 'id')
      user.setValue(node.login, 'login')
      user.setValue(node.avatarUrl, 'avatarUrl')
      edgeRecord.setLinkedRecord(user, 'node')
    } else {
      edgeRecord.setValue(null, 'node')
    }
    return edgeRecord
  })
  connection.setLinkedRecords(edges, 'edges')
}

function writeIssueRecord(store: RecordSourceProxy, issue: IssueRecordFields): RecordProxy {
  const record = store.get(issue.id) ?? store.create(issue.id, 'Issue')
  record.setValue(issue.id, 'id')
  record.setValue(issue.state, 'state')
  record.setValue(issue.stateReason, 'stateReason')
  record.setValue(issue.url, 'url')
  record.setValue(issue.databaseId, 'databaseId')
  record.setValue(issue.number, 'number')
  record.setValue(issue.title, 'title')
  // eslint-disable-next-line @github-ui/github-monorepo/no-unsafe-titlehtml -- Server-rendered HTML from the mutation response.
  record.setValue(issue.titleHTML, 'titleHTML')
  if (issue.closed !== undefined) record.setValue(issue.closed, 'closed')

  linkedRecord(store, record, 'issueDependenciesSummary', 'IssueDependenciesSummary').setValue(
    issue.issueDependenciesSummary.blockedBy,
    'blockedBy',
  )
  writeAssignees(store, record, issue.assignees)
  record.setLinkedRecord(writeRepository(store, issue.repository), 'repository')

  const summary = linkedRecord(store, record, 'subIssuesSummary', 'SubIssuesSummary')
  summary.setValue(issue.subIssuesSummary.total, 'total')
  summary.setValue(issue.subIssuesSummary.completed, 'completed')

  if (issue.issueType) {
    const issueType = store.get(issue.issueType.id) ?? store.create(issue.issueType.id, 'IssueType')
    issueType.setValue(issue.issueType.id, 'id')
    issueType.setValue(issue.issueType.name, 'name')
    issueType.setValue(issue.issueType.color, 'color')
    record.setLinkedRecord(issueType, 'issueType')
  } else {
    record.setValue(null, 'issueType')
  }

  if (issue.closedByPullRequestsReferences) {
    linkedRecord(
      store,
      record,
      'closedByPullRequestsReferences',
      'PullRequestConnection',
      CLOSED_BY_PULL_REQUESTS_ARGS,
    ).setValue(issue.closedByPullRequestsReferences.totalCount, 'totalCount')
  } else {
    record.setValue(null, 'closedByPullRequestsReferences', CLOSED_BY_PULL_REQUESTS_ARGS)
  }

  return record
}

function writeParentRecord(
  store: RecordSourceProxy,
  parent: ParentIssue,
  subIssuesSummary: {total: number; completed: number},
): void {
  const record = store.get(parent.id) ?? store.create(parent.id, 'Issue')
  record.setValue(parent.id, 'id')
  record.setValue(parent.databaseId, 'databaseId')
  record.setValue(parent.number, 'number')

  const repository = writeRepository(store, parent.repository)
  repository.setValue(parent.repository.nameWithOwner, 'nameWithOwner')
  repository.setValue(parent.repository.visibility, 'visibility')
  repository.setValue(parent.repository.viewerCanPush, 'viewerCanPush')
  record.setLinkedRecord(repository, 'repository')

  if (parent.parent) {
    const grandparent = store.get(parent.parent.id) ?? store.create(parent.parent.id, 'Issue')
    grandparent.setValue(parent.parent.id, 'id')
    record.setLinkedRecord(grandparent, 'parent')
  } else {
    record.setValue(null, 'parent')
  }

  linkedRecord(store, record, 'subIssues', 'IssueConnection').setValue(
    parent.subIssuesConnection.totalCount,
    'totalCount',
  )
  linkedRecord(store, record, 'subIssues', 'IssueConnection', SUB_ISSUES_ARGS).setLinkedRecords(
    (parent.subIssues.nodes ?? []).map(node => (node ? writeIssueRecord(store, node) : null)),
    'nodes',
  )

  const summary = linkedRecord(store, record, 'subIssuesSummary', 'SubIssuesSummary')
  summary.setValue(subIssuesSummary.total, 'total')
  summary.setValue(subIssuesSummary.completed, 'completed')
}

function writeConversionToRelay(
  environment: Environment,
  issue: SuccessfulIssue,
  subIssuesSummary: {total: number; completed: number},
): void {
  environment.commitUpdate(store => {
    writeIssueRecord(store, issue)
    writeParentRecord(store, issue.parent, subIssuesSummary)
  })
  installIssueBodyInRelay(environment, issue.parent)
}

function confirmedParentFields(issue: SuccessfulIssue) {
  const {parent} = issue
  const nodes = parent.subIssues.nodes ?? []
  const subIssuesSummary = {
    total: parent.subIssuesConnection.totalCount,
    completed: nodes.reduce((total, node) => total + (node?.closed ? 1 : 0), 0),
  }

  return {subIssuesSummary}
}

function subIssueFromResponse(data: CreateSubIssueFromChecklistItemMutationMutation): SuccessfulIssue {
  const payload = data.convertChecklistItemToSubIssue
  if (!payload) throw new Error('Create sub-issue from checklist item returned no payload')
  throwPayloadErrors(payload.errors, 'convertChecklistItemToSubIssue')

  const issue = payload.issue
  if (!issue) throw new Error('Create sub-issue from checklist item returned no issue')
  if (!issue.parent) throw new Error('Create sub-issue from checklist item returned no parent issue')

  return {...issue, parent: issue.parent}
}

export function useCreateSubIssueFromChecklistItemMutation(environment: Environment, parentIssueId: string) {
  const mutation = useMutation<ConversionResult, Error, CreateSubIssueMutationVariables>({
    mutationKey: connectionMutationKey(parentIssueId, SUB_ISSUES_CONNECTION_PATH),
    mutationFn: async ({owner, repository, issueNumber, body, position}) => {
      const input = {body, position: [...position]}
      const data = await postChecklistItemConversion<CreateSubIssueFromChecklistItemMutationMutation, typeof input>(
        {owner, repository, issueNumber},
        'checklist_item_sub_issues',
        input,
        createSubIssueFromChecklistItemQueryId,
      )

      return {data, issue: subIssueFromResponse(data)}
    },
    onSuccess: async ({issue}, {operation}) => {
      if (operation.type !== 'addToConnection') throw new Error('Invalid checklist sub-issue operation')
      const {subIssuesSummary} = confirmedParentFields(issue)
      writeConversionToRelay(environment, issue, subIssuesSummary)

      const {id: parentId, body: parentBody, bodyHTML, subIssues, subIssuesConnection} = issue.parent
      const {id: issueId, ...issueFields} = issue
      const optimisticOverlayEnabled = isSubIssuesOptimisticOverlayEnabled()

      await settleIssueFields({
        type: 'setFields',
        id: parentId,
        fields: optimisticOverlayEnabled
          ? {body: parentBody, bodyHTML}
          : {body: parentBody, bodyHTML, subIssues, subIssuesConnection, subIssuesSummary},
      })
      if (optimisticOverlayEnabled) {
        await notifySettlement(
          buildAddOperation({...buildSubIssueNodeFromServer(issue), [OPTIMISTIC_KEY]: operation.node.id}),
          parentId,
          SUB_ISSUES_CONNECTION_PATH,
        )
      }
      await settleIssueFields({
        type: 'setFields',
        id: issueId,
        fields: issueFields,
      })
    },
  })

  const {mutateAsync} = mutation
  const convert = useCallback(
    (variables: CreateSubIssueFromChecklistItemVariables) =>
      mutateAsync({...variables, operation: buildOptimisticAddOperation(variables.title)}),
    [mutateAsync],
  )

  return {...mutation, mutateAsync: convert}
}
